/**
 * One message in the conversation.
 *
 * Assistant replies render as full-width prose with the actions in a row
 * underneath, the way a 2026 assistant reads — the old screen put both roles in
 * gradient chat bubbles capped at 560px, which turned a formatted answer with
 * headings and bullets into a narrow ragged column. The user's own message
 * stays a bubble, right-aligned, because there it marks the turn.
 *
 * The streaming caret is a CSS pseudo-element on the last paragraph rather than
 * an extra node, so it cannot be left behind if a chunk arrives mid-render.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useI18n } from '../../../i18n';
import Badge from '../../../components/Atoms/Badge';
import { buildMarkdownComponents } from '../utils/markdownComponents';
import BuildCard from './BuildCard';
import InterviewCard from './InterviewCard';

const AssistantMessage = ({
  message,
  isLast,
  isStreaming,
  editing,
  onCopy,
  onRegenerate,
  onStartEdit,
  onCancelEdit,
  onSubmitEdit,
  onNavigate,
  run,
  isHeld,
  onRunBuild,
  onCancelBuild,
  onRenameClasses,
  interview,
}) => {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const [editText, setEditText] = useState(message.content || '');
  const editRef = useRef(null);

  const components = useMemo(() => buildMarkdownComponents({ onNavigate }), [onNavigate]);
  const isUser = message.role === 'user';
  const hasText = !!(message.content || '').trim();
  // Pending with nothing yet = still thinking; pending with text = streaming.
  const isThinking = message.pending && !hasText;

  useEffect(() => {
    if (editing) {
      setEditText(message.content || '');
      // Focus and put the caret at the end so a small tweak needs no clicking.
      const node = editRef.current;
      if (node) {
        node.focus();
        node.setSelectionRange(node.value.length, node.value.length);
      }
    }
  }, [editing, message.content]);

  const copy = useCallback(async () => {
    const ok = await onCopy?.(message.content || '');
    if (ok !== false) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    }
  }, [message.content, onCopy]);

  const handleEditKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSubmitEdit?.(message.id, editText);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onCancelEdit?.();
    }
  };

  // ---- an interview card ---------------------------------------------------
  if (message.kind === 'interview') {
    return (
      <article className="lf-msg lf-msg--card">
        <InterviewCard
          message={message}
          run={run}
          isHeld={isHeld}
          onAnswer={interview?.onAnswer}
          onReopen={interview?.onReopen}
          onConfirm={interview?.onConfirm}
          onCancel={interview?.onCancel}
          onRetryVocabulary={interview?.onRetryVocabulary}
          onOpenStudio={interview?.onOpenStudio}
          onQueueTraining={interview?.onQueueTraining}
        />
      </article>
    );
  }

  // ---- a build card --------------------------------------------------------
  // Branched BEFORE the user fork and before markdown: a card has a `content`
  // mirror for search, export and the model's history, but it must never be
  // RENDERED as that markdown.
  if (message.kind === 'build') {
    return (
      <article className="lf-msg lf-msg--card">
        <BuildCard
          message={message}
          run={run}
          isHeld={isHeld}
          onRun={onRunBuild}
          onCancel={onCancelBuild}
          onRenameClasses={onRenameClasses}
        />
      </article>
    );
  }

  // ---- the user's own message ---------------------------------------------
  if (isUser) {
    if (editing) {
      return (
        <article className="lf-msg lf-msg--user lf-msg--editing">
          <div className="lf-msg__edit">
            <textarea
              ref={editRef}
              // Bare for the same reason as the composer: `.lf-msg__edit`
              // around it owns the border and the focus ring, and without this
              // the textarea draws a second bordered box inside that one.
              className="lf-msg__edit-input lf-field-bare"
              value={editText}
              onChange={(event) => setEditText(event.target.value)}
              onKeyDown={handleEditKeyDown}
              rows={3}
              aria-label={t('assistant.message.editLabel')}
            />
            <div className="lf-msg__edit-actions">
              <button type="button" className="lf-msg__edit-cancel" onClick={onCancelEdit}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="lf-msg__edit-save"
                onClick={() => onSubmitEdit?.(message.id, editText)}
                disabled={!editText.trim()}
              >
                {t('assistant.message.saveAndResend')}
              </button>
            </div>
          </div>
        </article>
      );
    }

    return (
      <article className="lf-msg lf-msg--user" aria-label={t('assistant.message.yours')}>
        <div className="lf-msg__bubble">{message.content}</div>
        <div className="lf-msg__actions lf-msg__actions--user">
          <button
            type="button"
            className="lf-msg__action"
            onClick={copy}
            title={copied ? t('assistant.message.copied') : t('assistant.message.copy')}
            aria-label={copied ? t('assistant.message.copied') : t('assistant.message.copy')}
          >
            <i className={`fa ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="lf-msg__action"
            onClick={() => onStartEdit?.(message.id)}
            disabled={isStreaming}
            title={t('assistant.message.edit')}
            aria-label={t('assistant.message.edit')}
          >
            <i className="fa fa-pen" aria-hidden="true" />
          </button>
        </div>
      </article>
    );
  }

  // ---- the assistant's reply ----------------------------------------------
  return (
    <article
      className={`lf-msg lf-msg--assistant${message.error ? ' lf-msg--error' : ''}`}
      aria-label={t('assistant.message.reply')}
    >
      <header className="lf-msg__meta">
        <span className="lf-msg__mark" aria-hidden="true">
          <i className="fa fa-robot" />
        </span>
        <span className="lf-msg__author">{t('assistant.title')}</span>

        {message.contextUsed && (
          <Badge variant="info" icon="fa-database" className="lf-msg__badge">
            {t('assistant.badge.grounded')}
          </Badge>
        )}
        {message.engine === 'rulebased' && !message.error && (
          <Badge variant="warning" icon="fa-exclamation-triangle" className="lf-msg__badge">
            {t('assistant.badge.limited')}
          </Badge>
        )}
        {message.stopped && (
          <Badge variant="neutral" icon="fa-stop" className="lf-msg__badge">
            {t('assistant.badge.stopped')}
          </Badge>
        )}
      </header>

      {isThinking ? (
        <div className="lf-msg__typing" role="status" aria-live="polite">
          <span />
          <span />
          <span />
          <span className="lf-msg__typing-text">{t('assistant.message.thinking')}</span>
        </div>
      ) : (
        <div
          className={`lf-msg__body${message.pending ? ' lf-msg__body--streaming' : ''}`}
          // Announce only the finished reply: a live region on streaming text
          // makes a screen reader read every partial sentence aloud.
          aria-busy={message.pending ? 'true' : 'false'}
        >
          {message.error ? (
            <p className="lf-msg__error-text">
              <i className="fa fa-exclamation-triangle" aria-hidden="true" /> {message.content}
            </p>
          ) : (
            <ReactMarkdown components={components}>{message.content}</ReactMarkdown>
          )}
        </div>
      )}

      {message.truncatedReason && (
        <p className="lf-msg__notice">
          <i className="fa fa-exclamation-triangle" aria-hidden="true" />{' '}
          {message.truncatedReason}
        </p>
      )}

      {!message.pending && (
        <div className="lf-msg__actions">
          {hasText && !message.error && (
            <button
              type="button"
              className="lf-msg__action"
              onClick={copy}
              title={copied ? t('assistant.message.copied') : t('assistant.message.copy')}
              aria-label={copied ? t('assistant.message.copied') : t('assistant.message.copy')}
            >
              <i className={`fa ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden="true" />
              <span>{copied ? t('assistant.message.copied') : t('assistant.message.copy')}</span>
            </button>
          )}
          {isLast && (
            <button
              type="button"
              className="lf-msg__action"
              onClick={onRegenerate}
              disabled={isStreaming}
              title={t('assistant.message.regenerate')}
              aria-label={t('assistant.message.regenerate')}
            >
              <i className="fa fa-sync" aria-hidden="true" />
              <span>
                {message.error
                  ? t('assistant.message.retry')
                  : t('assistant.message.regenerate')}
              </span>
            </button>
          )}
        </div>
      )}
    </article>
  );
};

export default AssistantMessage;
